import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// material
import { Box, Tooltip, Typography, useMediaQuery } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import Logo from "../../../components/Logo";
import Avatar from "@mui/material/Avatar";
import BusinessIcon from "@mui/icons-material/Business";
import { accessKeys, hasAccess, hasSomeAccess, resetAccess, setAccess } from "../../../utils/userAccess";
import LicenseAgreementApprovalDialog from "./LicenseAgreementApprovalDialog";
import { useSnackbar } from "notistack";
import Preloader from "../../../components/Preloader";
import * as Sentry from "@sentry/react";
import BankIdLoginForm from "./BankIdLoginForm";
import EmailLoginFrom from "./EmailLoginForm";
import { useSearchParams } from "react-router-dom";
import { loadActiveIntegrations } from "../../../utils/integrations";
import BankIdLoginFormEComp from "./BankIdLoginFormEComp";
import LoginEComp from "./LoginEComp";
import { Info, Lock } from "@mui/icons-material";
import ExternalAuthLoginForm from "./ExternalAuthLoginForm";

// ----------------------------------------------------------------------

export default function LoginForm({ redirectPath, defaultLoginMethod, startQrCodeOnDesktop }) {
  // if (!openBankIdLogin && window.global.isEcomp) {
  //   throw new Error();
  // }

  sessionStorage.removeItem("courses");
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showLicenseAgreementForUser, setShowLicenseAgreementForUser] = useState(null);
  const [token, setToken] = useState(false);
  const [additionalData, setAdditionalData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const api = useApi();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const isDigitalLicenseContact = !!searchParams.get("digitalLicensePublishGroupId");
  const isDigitalLicensePublic = !!searchParams.get("digitalLicenseId");
  const isEntraIDLogin = !!searchParams.get("entraCode");

  const smallScreen = useMediaQuery("(max-width:700px)");
  const logoContainerClassName = window.global.isKs
    ? `permitLoginBoxHeader${smallScreen ? " small" : ""}`
    : "loginLogo";
  const logoSx = window.global.isKs && !smallScreen ? { width: "300px" } : {};

  useEffect(() => {
    async function loadDigitalLicenseLinkData(digitalLicensePublishGroupId, digitalLicenseId, key) {
      setIsLoading(true);
      resetAccess();
      const url = digitalLicensePublishGroupId
        ? `${import.meta.env.VITE_AUTH_URL}auth/digital-licenses/groups?digitalLicensePublishGroupId=${digitalLicensePublishGroupId}&key=${key}`
        : `${import.meta.env.VITE_AUTH_URL}auth/digital-licenses/public?digitalLicenseId=${digitalLicenseId}&key=${key}`;

      const response = await api.fetch(url, false, "GET");

      if (response.isSuccessful) {
        const { access, token } = response;
        setAccess(access);
        sessionStorage.setItem("apiToken", `Bearer ${token.token}`);
        sessionStorage.setItem("apiTokenExpires", token.expires);
        sessionStorage.setItem("lastConnection", Date.now());
        const pageUrl = digitalLicensePublishGroupId
          ? `/d/groups/${digitalLicensePublishGroupId}/licenses`
          : `/d/licenses/${digitalLicenseId}/0`;
        sessionStorage.setItem("eCompStartPageUrl", pageUrl);
        await Translate.loadApp(false);
        navigate(pageUrl);
      }
      setIsLoading(false);
    }

    const digitalLicensePublishGroupId = searchParams.get("digitalLicensePublishGroupId");
    const digitalLicenseId = searchParams.get("digitalLicenseId");
    const key = searchParams.get("key");
    const hasParams = key && (digitalLicensePublishGroupId || digitalLicenseId);
    const isDigitalLicenseLoadNeeded = window.global.isEcomp && hasParams;
    if (isDigitalLicenseLoadNeeded) {
      loadDigitalLicenseLinkData(digitalLicensePublishGroupId, digitalLicenseId, key);
    }
  }, [searchParams, api, navigate]);

  useEffect(() => {
    async function exchangeEntraCode(entraCode) {
      setIsLoading(true);
      resetAccess();
      const url = `${import.meta.env.VITE_AUTH_URL}auth/entra/exchange?entraCode=${entraCode}`;
      const response = await api.fetch(url, false, "GET");

      if (response.isSuccessful) {
        localStorage.setItem("eportLoginMethod", "external");
        onLoginSuccess(response);
      } else {
        navigate("/login", { replace: true });
      }
      setIsLoading(false);
    }

    const entraCode = searchParams.get("entraCode");
    if (!!entraCode) {
      exchangeEntraCode(entraCode);
    }
  }, [searchParams, api, navigate]);

  async function handleSelectUser(user, accountLoginToken) {
    if (user.hasUnapprovedLicenseAgreements) {
      setShowLicenseAgreementForUser(user);
    } else if (user.isLocked && user.isLockedMessage) {
      // A user that is locked shouldn't be selectable. But when there is only one user we auto-select it.
      // In that case we will display the lock message like this instead since the user list is not shown.
      const errorMessage = enqueueSnackbar(user.isLockedMessage ?? Translate.get("SomethingFailed"), {
        variant: "error",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(errorMessage),
      });
      navigate("/", { replace: true });
    } else {
      setIsLoading(true);
      resetAccess();
      //console.log("selectedUser", selectedUser);
      //console.log("accountLoginToken", token);
      // (url, data = false, method = 'POST', token = false, json = true) {
      const data = await api.fetch(
        `${import.meta.env.VITE_AUTH_URL}auth/login/select-user`,
        user,
        "POST",
        `Bearer ${accountLoginToken}`,
      );
      if (data.identityToken !== null && data.identityToken?.token) {
        //console.log(data);
        //console.log(`Bearer ${data.identityToken.token}`);
        // sessionStorage.setItem('apiToken', 'Bearer ' + data.identityToken.token);
        sessionStorage.setItem("apiToken", `Bearer ${data.identityToken.token}`);
        sessionStorage.setItem("apiTokenExpires", data.identityToken.expires);
        //Logout mess -  see DashboardTimer.js
        sessionStorage.setItem("lastConnection", Date.now());
        ////console.log('Timer reset', Date.now());
        //---------------------------------------

        sessionStorage.setItem("apiRefreshToken", data.refreshToken.token);
        sessionStorage.setItem("companyName", user.companyName);
        sessionStorage.setItem("ePortUserId", user.ePortUserId);
        sessionStorage.removeItem("eCompStartPageUrl");
        Sentry.setUser({
          email: additionalData.email,
          id: user.ePortUserId,
        });
        setAccess(data.access);
        loadActiveIntegrations();
        Translate.loadApp(false).then(() => {
          if (redirectPath) {
            window.location = redirectPath;
          }
          //console.log("redirect start");
          /*  console.log(
              "HAS ACCESS courseAdmin--------------",
              hasAccess(accessKeys.courseAdmin)
            );
            console.log(
              "HAS ACCESS dashboard--------------",
              hasAccess(accessKeys.dashboard)
            ); */
          if (!hasAccess(accessKeys.courseAdmin)) {
            hasAccess(accessKeys.dashboard)
              ? navigate("/dashboard/app", { replace: true })
              : hasAccess(accessKeys.actorPermit)
                ? navigate("/dashboard/app", { replace: true })
                : navigate("/dashboard/licenseregistration", { replace: true });
          } else {
            hasAccess(accessKeys.dashboard)
              ? navigate("/dashboard/app", { replace: true })
              : navigate("/dashboard/courses", { replace: true });
          }
        });
      } else {
        // error
        sessionStorage.removeItem("apiToken");
        const errorMessage = enqueueSnackbar(Translate.get(data.errorMessageTranslationKey ?? "SomethingFailed"), {
          variant: "error",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(errorMessage),
        });
        navigate("/", { replace: true });
      }
    }
  }

  async function handleDigitalLicensesResponse({
    eCompToken,
    hasDigitalLicenses,
    hasDigitalPermits,
    access,
    eCompUserId,
  }) {
    if (!hasDigitalLicenses && !hasDigitalPermits) {
      const errorMessage = enqueueSnackbar(Translate.get("SomethingFailed"), {
        variant: "error",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(errorMessage),
      });
      navigate("/", { replace: true });
    }
    resetAccess();
    setAccess(access);
    sessionStorage.setItem("apiToken", `Bearer ${eCompToken.token}`);
    sessionStorage.setItem("apiTokenExpires", eCompToken.expires);
    sessionStorage.setItem("lastConnection", Date.now());
    const startPageUrl = `/d?startTab=${hasDigitalLicenses ? 0 : 1}`;
    sessionStorage.setItem("eCompStartPageUrl", startPageUrl);
    sessionStorage.removeItem("apiRefreshToken");
    Sentry.setUser({
      id: eCompUserId,
    });
    await Translate.loadApp(false);
    if (redirectPath) {
      window.location = redirectPath;
    } else {
      navigate(startPageUrl);
    }
  }

  async function onLoginSuccess(data) {
    if (window.global.isEcomp) {
      handleDigitalLicensesResponse(data);
    } else {
      const { name, email, accountLoginToken, users: ePortUsers } = data;
      sessionStorage.setItem("userFullName", name);
      sessionStorage.setItem("userEmail", email);
      sessionStorage.removeItem("logoURL");
      setAdditionalData({ name: name, email: email });
      setToken(accountLoginToken.token);
      if (ePortUsers.length > 1) {
        setUsers(ePortUsers);
      } else {
        handleSelectUser(ePortUsers[0], accountLoginToken.token);
      }
    }
  }

  if (isDigitalLicenseContact || isDigitalLicensePublic || (isEntraIDLogin && users.length === 0)) {
    return isLoading ? <Preloader invisible={true} /> : null;
  }

  if (showLicenseAgreementForUser) {
    return (
      <LicenseAgreementApprovalDialog
        user={showLicenseAgreementForUser}
        accountLoginToken={token}
        open={true}
        onClose={() => setShowLicenseAgreementForUser(null)}
        onComplete={() =>
          handleSelectUser(
            {
              ...showLicenseAgreementForUser,
              hasUnapprovedLicenseAgreements: false,
            },
            token,
          )
        }
      />
    );
  }

  if (users && users.length > 1) {
    return (
      <>
        {isLoading && <Preloader invisible={true} />}
        <div className={"loginBox"}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" gutterBottom>
              {Translate.get("SelectUserCompany")}
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>{Translate.get("MultipleAccountsConnected")}</Typography>
          </Box>
          <List>
            {users.map((user, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  className={"stdListButton" + (user.isLocked ? " disabledListItemWithChildHover" : "")}
                  onClick={() => handleSelectUser(user, token)}
                  disabled={user.isLocked}
                >
                  <ListItemIcon>
                    <Avatar>
                      <BusinessIcon />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText primary={user.companyName} secondary={parseInt(user.ePortUserId)} />
                  {user.isLocked && user.isLockedMessage && (
                    <span
                      style={{ display: "inline-flex", pointerEvents: "auto" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title={user.isLockedMessage}>
                        <Lock />
                      </Tooltip>
                    </span>
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      </>
    );
  }

  if (window.global.isEcomp) {
    return <LoginEComp onLoginSuccess={onLoginSuccess} startQrCodeOnDesktop={startQrCodeOnDesktop} />;
  } else {
    return (
      <div
        className={"loginBox" + (defaultLoginMethod === "external" ? " externalAuth" : "")}
        style={{ marginTop: "1em", marginBottom: "1em" }}
      >
        <div className={logoContainerClassName}>
          <Logo sx={logoSx} />
          {window.global.isKs && <div>{Translate.get("LoginPermitAdmin")}</div>}
        </div>
        {(defaultLoginMethod === "email" || !defaultLoginMethod) && (
          <EmailLoginFrom onLoginSuccess={onLoginSuccess} redirectPath={redirectPath} />
        )}
        {defaultLoginMethod === "bankid" && (
          <BankIdLoginForm onLoginSuccess={onLoginSuccess} startQrCodeOnDesktop={startQrCodeOnDesktop} />
        )}
        {defaultLoginMethod === "external" && <ExternalAuthLoginForm onLoginSuccess={onLoginSuccess} />}
        {window.global.isKs && (
          <div className="permitLoginBoxFooter">
            <a href="/login/permit-responsible-view">{Translate.get("SwitchToPermitResponsibleView")}</a>
          </div>
        )}
      </div>
    );
  }
}
